import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { ShoppingCart, Plus, Minus, Trash2, CheckCircle2, AlertCircle } from "lucide-react";
import SellerLayout, { useSellerSession } from "./layout";
import type { Client, Product } from "@shared/schema";

interface CartItem {
  productId: string;
  productName: string;
  quantity: number;
  price: string;
}

export default function SellerNewOrder() {
  const { data: seller } = useSellerSession();
  const { toast } = useToast();
  const [selectedClientId, setSelectedClientId] = useState<string>("");
  const [cart, setCart] = useState<CartItem[]>([]);
  const [notes, setNotes] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const { data: clients, isLoading: clientsLoading } = useQuery<Client[]>({ queryKey: ["/api/seller/clients"] });
  const { data: products, isLoading: productsLoading } = useQuery<Product[]>({ queryKey: ["/api/seller/products"] });
  const { data: orderSettings } = useQuery<{ orderingDisabled?: boolean; closedOrderMessage?: string }>({
    queryKey: ["/api/settings/order-window"],
  });

  const createMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/seller/orders", {
      clientId: selectedClientId,
      items: cart.map(i => ({ productId: i.productId, quantity: i.quantity, price: i.price })),
      notes: notes || undefined,
    }),
    onSuccess: () => {
      toast({ title: "Order skapad!" });
      queryClient.invalidateQueries({ queryKey: ["/api/seller/orders/today"] });
      setCart([]);
      setSelectedClientId("");
      setNotes("");
      setSubmitted(true);
    },
    onError: async (error: any) => {
      const message = error?.message || "Kunde inte skapa order.";
      toast({ title: "Fel", description: message, variant: "destructive" });
    },
  });

  const addToCart = (product: Product) => {
    setCart(prev => {
      const existing = prev.find(i => i.productId === product.id);
      if (existing) {
        return prev.map(i => i.productId === product.id ? { ...i, quantity: i.quantity + 1 } : i);
      }
      return [...prev, { productId: product.id, productName: product.name, quantity: 1, price: product.unitPrice }];
    });
  };

  const updateQty = (productId: string, delta: number) => {
    setCart(prev => {
      const updated = prev.map(i => i.productId === productId ? { ...i, quantity: Math.max(0, i.quantity + delta) } : i);
      return updated.filter(i => i.quantity > 0);
    });
  };

  const removeFromCart = (productId: string) => {
    setCart(prev => prev.filter(i => i.productId !== productId));
  };

  if (!seller?.canCreateOrders) {
    return (
      <SellerLayout>
        <div className="flex flex-col items-center justify-center min-h-[50vh] text-center">
          <ShoppingCart className="h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-muted-foreground">Du har inte behörighet att skapa ordrar.</p>
        </div>
      </SellerLayout>
    );
  }

  if (orderSettings?.orderingDisabled) {
    return (
      <SellerLayout>
        <div className="space-y-6">
          <div>
            <h1 className="text-2xl font-bold">Ny order</h1>
            <p className="text-muted-foreground">Beställningar är tillfälligt stängda</p>
          </div>
          <Card className="border-amber-500/30 bg-amber-500/5" data-testid="card-seller-ordering-disabled">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" />
                <p className="text-sm whitespace-pre-wrap">
                  {orderSettings.closedOrderMessage || "Vi tar inte emot beställningar just nu."}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </SellerLayout>
    );
  }

  if (submitted) {
    return (
      <SellerLayout>
        <div className="flex flex-col items-center justify-center min-h-[50vh] text-center gap-4">
          <CheckCircle2 className="h-16 w-16 text-green-500" />
          <h2 className="text-2xl font-bold">Order skapad!</h2>
          <p className="text-muted-foreground">Ordern har skickats in och väntar på bekräftelse.</p>
          <Button onClick={() => setSubmitted(false)} data-testid="button-new-order-again">Skapa ny order</Button>
        </div>
      </SellerLayout>
    );
  }

  const cartTotal = cart.reduce((sum, i) => sum + parseFloat(i.price || "0") * i.quantity, 0);

  return (
    <SellerLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Ny order</h1>
          <p className="text-muted-foreground">Välj kund och artiklar för att skapa en order</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: client + products */}
          <div className="lg:col-span-2 space-y-4">
            {/* Client selection */}
            <Card>
              <CardHeader><CardTitle className="text-base">Välj kund</CardTitle></CardHeader>
              <CardContent>
                {clientsLoading ? <Skeleton className="h-9 w-full" /> : (
                  <Select value={selectedClientId} onValueChange={setSelectedClientId}>
                    <SelectTrigger data-testid="select-client">
                      <SelectValue placeholder="Välj kund..." />
                    </SelectTrigger>
                    <SelectContent>
                      {clients?.map(c => (
                        <SelectItem key={c.id} value={c.id} data-testid={`option-client-${c.id}`}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </CardContent>
            </Card>

            {/* Product list */}
            <Card>
              <CardHeader><CardTitle className="text-base">Artiklar</CardTitle></CardHeader>
              <CardContent>
                {productsLoading ? (
                  <div className="space-y-2">{[1, 2, 3].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>
                ) : products && products.length > 0 ? (
                  <div className="space-y-2">
                    {products.map(product => {
                      const inCart = cart.find(i => i.productId === product.id);
                      return (
                        <div key={product.id} className="flex items-center justify-between rounded-md border px-3 py-2" data-testid={`row-product-${product.id}`}>
                          <div>
                            <p className="font-medium text-sm">{product.name}</p>
                            <p className="text-xs text-muted-foreground">{product.unitPrice} kr / {product.sellUnit}</p>
                          </div>
                          {inCart ? (
                            <div className="flex items-center gap-1">
                              <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => updateQty(product.id, -1)} data-testid={`button-decrease-${product.id}`}>
                                <Minus className="h-3 w-3" />
                              </Button>
                              <span className="w-8 text-center text-sm font-medium" data-testid={`qty-${product.id}`}>{inCart.quantity}</span>
                              <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => updateQty(product.id, 1)} data-testid={`button-increase-${product.id}`}>
                                <Plus className="h-3 w-3" />
                              </Button>
                            </div>
                          ) : (
                            <Button size="sm" variant="outline" onClick={() => addToCart(product)} data-testid={`button-add-${product.id}`}>
                              <Plus className="h-3.5 w-3.5 mr-1" /> Lägg till
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">Inga produkter tillgängliga</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right: cart summary */}
          <div className="space-y-4">
            <Card>
              <CardHeader><CardTitle className="text-base">Varukorg ({cart.length})</CardTitle></CardHeader>
              <CardContent>
                {cart.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">Inga artiklar tillagda</p>
                ) : (
                  <div className="space-y-2">
                    {cart.map(item => (
                      <div key={item.productId} className="flex items-center justify-between text-sm" data-testid={`cart-item-${item.productId}`}>
                        <div className="flex-1 min-w-0">
                          <p className="truncate font-medium">{item.productName}</p>
                          <p className="text-xs text-muted-foreground">{item.quantity} × {item.price} kr</p>
                        </div>
                        <div className="flex items-center gap-2 ml-2">
                          <span className="font-medium">{(parseFloat(item.price) * item.quantity).toFixed(2)} kr</span>
                          <Button size="icon" variant="ghost" className="h-6 w-6 text-destructive" onClick={() => removeFromCart(item.productId)} data-testid={`button-remove-${item.productId}`}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    <div className="border-t pt-2 mt-2 flex justify-between font-semibold">
                      <span>Totalt</span>
                      <span data-testid="cart-total">{cartTotal.toFixed(2)} kr</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-4 space-y-3">
                <div>
                  <Label htmlFor="notes">Anteckning</Label>
                  <Textarea
                    id="notes"
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    placeholder="Valfri anteckning till ordern..."
                    className="mt-1"
                    data-testid="input-order-notes"
                  />
                </div>
                <Button
                  className="w-full"
                  onClick={() => createMutation.mutate()}
                  disabled={!selectedClientId || cart.length === 0 || createMutation.isPending}
                  data-testid="button-submit-order"
                >
                  <ShoppingCart className="h-4 w-4 mr-2" />
                  {createMutation.isPending ? "Skapar..." : "Skapa order"}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </SellerLayout>
  );
}
